import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { ActionButton } from 'components';
import { IconButton, Menu, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    actionIcons: {
        fontSize: 25,
        color: 'inherit',
        marginRight: 10,
    },
    menuList: {
        minWidth: 200,
    },
    menuIcon: {
        fontSize: 28,
    },
    itemText: {
        display: 'flex',
        alignItems: 'center',
    },
    menuItem: {
        color: theme.palette.grey[700],
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
    },
    customButton: {
        '&:hover': {
            backgroundColor: theme.palette.common.white,
        },
    },
}));

const ActionsMenu = props => {
    const {
        open,
        handleOpen,
        controlled,
        buttonStyle = 'icon',
        buttonText,
        justify = 'flex-end',
        autoClose,
        containerProps,
        buttonProps,
    } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const buttonRef = useRef(null);
    const classes = useStyles();

    const handleClick = useCallback(
        ({ currentTarget }) => {
            if (controlled) {
                handleOpen();
            } else {
                setAnchorEl(currentTarget);
            }
        },
        [controlled, handleOpen]
    );

    const handleClose = useCallback(() => {
        if (controlled) {
            handleOpen(false);
        } else {
            setAnchorEl(null);
        }
    }, [handleOpen, controlled]);

    useEffect(() => {
        if (controlled && !anchorEl) {
            setAnchorEl(buttonRef.current);
        }
    }, [controlled, anchorEl]);

    return (
        <Box display="flex" justifyContent={justify} width="100%" {...containerProps}>
            {buttonStyle === 'text' ? (
                <ActionButton
                    buttonRef={buttonRef}
                    onClick={handleClick}
                    className={classes.customButton}
                    {...buttonProps}
                >
                    {buttonText} <icons.ChevronDown />
                </ActionButton>
            ) : (
                <IconButton
                    disableRipple
                    size="small"
                    ref={buttonRef}
                    variant="contained"
                    color="primary"
                    onClick={handleClick}
                    {...buttonProps}
                >
                    <icons.DotsVerticalCircleOutline className={classes.menuIcon} />
                </IconButton>
            )}
            <Menu
                id="customized-menu"
                anchorEl={anchorEl}
                ripple={null}
                keepMounted
                classes={{ list: classes.menuList }}
                open={controlled ? open : !!anchorEl}
                onClose={handleClose}
                MenuListProps={{ disablePadding: true }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                {React.Children.map(
                    props.children,
                    child =>
                        React.isValidElement(child) &&
                        React.cloneElement(child, {
                            ...child.props,
                            classes,
                            onClick: e => {
                                child.props.onClick(e);
                                if (autoClose) handleClose();
                            },
                        })
                )}
            </Menu>
        </Box>
    );
};

ActionsMenu.propTypes = {
    open: PropTypes.bool,
    autoClose: PropTypes.bool,
    handleOpen: PropTypes.func,
    controlled: PropTypes.bool,
    buttonText: PropTypes.string,
    buttonStyle: PropTypes.oneOf(['icon', 'text']),
};

export default ActionsMenu;
